import React, { useState, useEffect } from "react";
import { X } from "lucide-react";
import CheckCircle from "../../assets/tic.svg";

// Modal 1: Initial Warning/Information
export const TransferOwnershipModal1 = ({ onClose, onContinue }) => {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex justify-center items-center font-['Manrope'] py-[30px] overflow-y-auto">
      <div className="w-[500px] bg-white rounded-[20px] overflow-hidden max-h-full flex flex-col">
        {/* Header */}
        <div className="px-6 py-6 bg-gradient-to-br from-white to-[#D9E9FE] border-b border-[#DAEBFF] flex justify-between items-center flex-shrink-0">
          <h2 className="text-[18px] font-bold text-gray-900">
            Transfer Admin Ownership
          </h2>
          <button
            onClick={onClose}
            className="w-[22px] h-[22px] flex items-center justify-center"
          >
            <X size={18} className="text-gray-900" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 pt-6 flex flex-col overflow-y-auto scrollbar-hide">
          <p className="text-gray-500 text-[16px] font-medium mb-5">
            You are about to transfer the ownership of this school/ organization
            account to another admin.
          </p>

          {/* What this means */}
          <div className="flex flex-col gap-2 mb-5">
            <h3 className="text-gray-900 text-base font-semibold">
              What this means:
            </h3>
            <ul className="list-disc pl-6 text-gray-900 text-[16px] font-medium space-y-1">
              <li>
                All administrative rights, including student management, school
                settings, and reporting, will be moved to the new admin.
              </li>
              <li>
                Once the transfer is complete, you will no longer have access to
                the Admin Dashboard or any private school data.
              </li>
              <li>
                All student details and records will remain completely intact.
              </li>
            </ul>
          </div>

          {/* After transferring */}
          <div className="flex flex-col gap-2 mb-6">
            <h3 className="text-gray-900 text-base font-semibold">
              After transferring ownership:
            </h3>
            <ul className="list-disc pl-6 text-gray-900 text-base font-medium space-y-1">
              <li>
                The new admin will become the primary owner and main point of
                contact.
              </li>
              <li>
                This action cannot be undone. Please ensure the recipient's
                email address is correct before proceeding.
              </li>
            </ul>
          </div>
        </div>
        <div className="px-6 pb-6 pt-2">
          {/* Footer */}
          <div className="flex items-center gap-4">
            <button
              onClick={onClose}
              className="flex-1 h-14 bg-white border border-primary rounded-[10px] text-primary text-base font-medium hover:bg-blue-50"
            >
              Cancel
            </button>
            <button
              onClick={onContinue}
              className="flex-1 h-14 bg-primary rounded-[10px] text-white text-base font-medium hover:bg-blue-600"
            >
              Continue
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Modal 2: Enter New Admin Details
export const TransferOwnershipModal2 = ({ onClose, onBack, onContinue }) => {
  const [formData, setFormData] = useState({
    adminName: "",
    adminTitle: "",
    email: "",
  });

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleContinue = () => {
    onContinue(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex justify-center items-center font-['Manrope'] py-[30px] overflow-y-auto">
      <div className="w-[500px] bg-white rounded-[20px] overflow-hidden max-h-full flex flex-col">
        <div className="px-6 py-6 bg-gradient-to-br from-white to-[#D9E9FE] border-b border-[#DAEBFF] flex justify-between items-center">
          <h2 className="text-[18px] font-bold text-gray-900">
            Transfer Admin Ownership
          </h2>
          <button
            onClick={onClose}
            className="w-[22px] h-[22px] flex items-center justify-center"
          >
            <X size={18} className="text-gray-900" />
          </button>
        </div>

        <div className="px-6 py-6 flex flex-col overflow-y-auto scrollbar-hide mb-4">
          <div className="flex flex-col gap-5">
            <p className="text-gray-500 text-base font-medium">
              Enter the name, title, and email of the person you are
              transferring this account to.
            </p>

            <div className="flex flex-col gap-4">
              <div className="flex flex-col">
                <div className="px-4 py-3.5 bg-white border border-gray-300 rounded-[10px] flex flex-col gap-1.5">
                  <label className="text-gray-400 text-sm font-medium">
                    Admin Name
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., John Doe"
                    value={formData.adminName}
                    onChange={(e) => handleChange("adminName", e.target.value)}
                    className="text-gray-900 text-base font-medium outline-none"
                  />
                </div>
              </div>

              <div className="flex flex-col">
                <div className="px-4 py-3.5 bg-white border border-gray-300 rounded-[10px] flex flex-col gap-1.5">
                  <label className="text-gray-400 text-sm font-medium">
                    Admin Title
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., Principal or Head of Department"
                    value={formData.adminTitle}
                    onChange={(e) => handleChange("adminTitle", e.target.value)}
                    className="text-gray-900 text-base font-medium outline-none"
                  />
                </div>
              </div>

              <div className="flex flex-col">
                <div className="px-4 py-3.5 bg-white border border-gray-300 rounded-[10px] flex flex-col gap-1.5">
                  <label className="text-gray-400 text-sm font-medium">
                    Email address
                  </label>
                  <input
                    type="email"
                    placeholder="your@example.com"
                    value={formData.email}
                    onChange={(e) => handleChange("email", e.target.value)}
                    className="text-gray-900 text-base font-medium outline-none"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-4 px-6 pb-6">
          <button
            onClick={onBack}
            className="flex-1 h-14 px-4 py-2 bg-white border border-primary rounded-[10px] text-primary text-base font-medium hover:bg-blue-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleContinue}
            className="flex-1 h-14 px-4 py-2 bg-primary rounded-[10px] text-white text-base font-medium hover:bg-blue-600 transition-colors"
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
};

// Modal 3: Confirmation
export const TransferOwnershipModal3 = ({
  onClose,
  onBack,
  onContinue,
  formData,
  currentEmail,
}) => {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex justify-center items-center font-['Manrope'] py-[30px] overflow-y-auto">
      <div className="w-[500px] bg-white rounded-[20px] overflow-hidden max-h-full flex flex-col">
        <div className="px-6 py-6 bg-gradient-to-br from-white to-[#D9E9FE] border-b border-[#DAEBFF] flex justify-between items-center">
          <h2 className="text-[18px] font-bold text-gray-900">
            Transfer Admin Ownership
          </h2>
          <button
            onClick={onClose}
            className="w-[22px] h-[22px] flex items-center justify-center"
          >
            <X size={18} className="text-gray-900" />
          </button>
        </div>

        <div className="px-6 py-6 flex flex-col gap-8 overflow-y-auto scrollbar-hide">
          <div className="flex flex-col gap-5">
            <p className="text-gray-500 text-base font-medium">
              Make sure the details below are correct before you continue.
            </p>

            <div className="flex flex-col gap-2">
              <h3 className="text-gray-900 text-base font-semibold">
                Current Email
              </h3>
              <div className="px-4 py-3.5 bg-gray-100 border border-gray-300 rounded-[10px] flex flex-col gap-1.5">
                <label className="text-gray-400 text-sm font-medium">
                  Email address
                </label>
                <div className="text-gray-500 text-base font-medium">
                  {currentEmail}
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <h3 className="text-gray-900 text-base font-semibold">
                New Email
              </h3>

              <div className="px-4 py-3.5 bg-white border border-gray-300 rounded-[10px] flex flex-col gap-1.5">
                <label className="text-gray-400 text-sm font-medium">
                  Email address
                </label>
                <input
                  type="email"
                  placeholder="e.g., admin@school.edu"
                  value={formData.email}
                  className="text-gray-900 text-base font-medium outline-none bg-transparent"
                />
              </div>
            </div>
          </div>
        </div>
        <div className="px-6 pb-6 flex items-center gap-4">
          <button
            onClick={onBack}
            className="flex-1 h-14 px-4 py-2 bg-white border border-primary rounded-[10px] text-primary text-base font-medium hover:bg-blue-50 transition-colors"
          >
            Back
          </button>
          <button
            onClick={onContinue}
            className="flex-1 h-14 px-4 py-2 bg-primary rounded-[10px] text-white text-base font-medium hover:bg-blue-600 transition-colors"
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
};

// Modal 4: Success
export const TransferOwnershipModal4 = ({ onClose, newAdminEmail }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 3000);

    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex justify-center items-center font-['Manrope'] py-[30px] overflow-y-auto">
      <div className="w-[500px] bg-white rounded-[20px] overflow-hidden max-h-full overflow-y-auto scrollbar-hide">
        <div className="px-6 py-12 flex flex-col gap-8">
          <div className="flex flex-col items-center gap-16">
            <div className="flex flex-col items-center gap-4">
              <div className="w-[100px] h-[100px] flex items-center justify-center">
                <img src={CheckCircle} alt="Success" />
              </div>
              <div className="flex flex-col gap-2">
                <h2 className="text-center text-gray-900 text-2xl font-bold">
                  Ownership Transferred Successfully!
                </h2>
                <p className="text-center text-gray-500 text-lg font-semibold">
                  You have successfully transferred the admin account to
                  {" newadmin@example.com "}
                  {newAdminEmail}.
                </p>
              </div>
            </div>

            <p className="text-center text-gray-900 text-sm font-semibold">
              Keep building your story — your journey is officially yours.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
